// Anthropic /v1/messages ↔ Vercel AI SDK. One turn per request; Claude Code owns the tool loop.
import { createHash, randomUUID } from 'node:crypto';
import { streamText, generateText, tool, jsonSchema } from 'ai';
import type { LanguageModel, ModelMessage } from 'ai';
import {
  sseChunk,
  encodeToolUseId,
  splitToolUseId,
  serializeToolResultContent,
  silenceSdkWarnings,
  type FullStreamPart,
  grabRoundTripSignature,
} from './proxy-shared.js';
import {
  deepMergeProviderOptions,
  effortProviderOptions,
  thinkingProviderOptions,
  type ReasoningMetadata,
} from './provider-factory.js';
import { resolveUpstreamTools } from './tool-search.js';
import { sanitizeToolInput } from './tool-input-sanitize.js';
import { sanitizeToolSchema } from './tool-schema-sanitize.js';
import { VERTEX_ANTHROPIC_NPM } from './constants.js';
import type { AnthropicRequestMessage, AnthropicToolDefinition } from './proxy-types.js';
import { anthropicErrorType, sdkUpstreamErrorDetails, upstreamHttpStatus } from './upstream-error.js';
import { upstreamRequestBudget } from './upstream-retry.js';
import { trackUpstreamAttempts } from './upstream-attempts.js';
import { emitParentNotice } from './parent-notice.js';
import { CLAUDE_CODE_COMPACT_PROMPT_MARKERS } from './claude-code-compact-prompt.js';
import { CLAUDE_CODE_BILLING_HEADER_PREFIX } from './oauth/claude-identity.js';
import { OpenAiThinkingBlock, openAiReasoningItemId, restoreOpenAiThinking } from './openai-thinking.js';

export { silenceSdkWarnings };

export type SdkTranslationErrorSignature =
  | 'reasoning_part_not_found'
  | 'text_part_not_found';

/** Classify privacy-safe AI SDK stream-state errors without logging dynamic part ids. */
export function sdkTranslationErrorSignature(error: unknown): SdkTranslationErrorSignature | undefined {
  const message = error instanceof Error
    ? error.message
    : typeof error === 'string' ? error : undefined;
  if (!message) return undefined;
  if (/\breasoning part \S+ not found\b/i.test(message)) return 'reasoning_part_not_found';
  if (/\btext part \S+ not found\b/i.test(message)) return 'text_part_not_found';
  return undefined;
}

// ── Anthropic request shapes (only the fields we read) ───────────────────────
interface AnthropicBlock {
  type: string;
  text?: string;
  thinking?: string;
  signature?: string;
  id?: string;
  name?: string;
  input?: Record<string, unknown>;
  tool_use_id?: string;
  content?: unknown;
  source?: { type: 'base64' | 'url'; media_type?: string; data?: string; url?: string };
  cache_control?: { type?: string; ttl?: string };
  // internal: resolved tool name for a tool_result, set by annotateToolNames
  _name?: string;
}
interface AnthropicMsg { role: 'user' | 'assistant' | 'system'; content: string | AnthropicBlock[]; }
interface AnthropicTool {
  name: string;
  description?: string;
  input_schema: Record<string, unknown>;
  cache_control?: { type?: string; ttl?: string };
}
export interface AnthropicRequest {
  model: string;
  system?: string | Array<string | { text?: string; cache_control?: { type?: string; ttl?: string } }>;
  messages: AnthropicMsg[];
  tools?: AnthropicTool[];
  tool_choice?: { type: 'auto' | 'any' | 'tool'; name?: string };
  max_tokens?: number;
  temperature?: number;
  stream?: boolean;
  thinking?: { type?: string; budget_tokens?: number };
  output_config?: { effort?: string };
  metadata?: { user_id?: unknown };
  diagnostics?: unknown;
}

export interface TranslateRequestOptions {
  /** Fallback when the client omits effort (e.g. Claude Desktop gateway). */
  defaultEffort?: string;
  reasoningMetadata?: ReasoningMetadata;
  /** ChatGPT Codex OAuth requires instructions and manages its own output limit. */
  openAiOAuth?: boolean;
  /** Fallback session identity from X-Claude-Code-Session-Id. Body metadata wins. */
  claudeSessionId?: string;
  /** Hard cap on tools sent to the provider (e.g. Groq: 128). Excess tools are silently dropped. */
  maxTools?: number;
  /** Immediate trace-log sink; diagnostics must call it before terminal-warning suppression. */
  log?: (message: string) => void;
}

const CLAUDE_SESSION_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function validClaudeSessionId(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return CLAUDE_SESSION_ID_RE.test(trimmed) ? trimmed.toLowerCase() : undefined;
}

/** Extract Claude Code's stable session UUID without accepting arbitrary metadata. */
export function extractClaudeSessionId(
  body: Pick<AnthropicRequest, 'metadata'>,
  headerFallback?: string,
): string | undefined {
  const userId = body.metadata?.user_id;
  if (typeof userId === 'string') {
    try {
      const parsed = JSON.parse(userId) as { session_id?: unknown };
      const fromMetadata = validClaudeSessionId(parsed?.session_id);
      if (fromMetadata) return fromMetadata;
    } catch {
      // Malformed or non-JSON metadata is ignored; the header remains usable.
    }
  }
  return validClaudeSessionId(headerFallback);
}

const CLAUDE_AGENT_ID_RE = /^[A-Za-z0-9._-]{1,128}$/;

/**
 * Claude Code 2.1.268 marks every request from an in-process subagent with
 * `x-claude-code-agent-id` (and its parent's id in `x-claude-code-parent-agent-id`);
 * the main agent sends neither. The value is opaque, so only its shape is checked.
 */
export function extractClaudeAgentIds(
  headers: Record<string, string | string[] | undefined>,
): { claudeAgentId?: string; claudeParentAgentId?: string } {
  const read = (name: string): string | undefined => {
    const raw = headers[name];
    const value = (Array.isArray(raw) ? raw[0] : raw)?.trim();
    return value && CLAUDE_AGENT_ID_RE.test(value) ? value : undefined;
  };
  return {
    claudeAgentId: read('x-claude-code-agent-id'),
    claudeParentAgentId: read('x-claude-code-parent-agent-id'),
  };
}

/** Opaque prompt-cache partition derived from a Claude session UUID. */
export function claudeSessionPromptCacheKey(sessionId: string): string {
  return 'relay-session-' + createHash('sha256').update(sessionId).digest('hex').slice(0, 32);
}

/** Read reasoning effort from an Anthropic-format request body. */
export function anthropicEffortFromRequest(body: AnthropicRequest): string | undefined {
  const effort = body.output_config?.effort;
  if (typeof effort === 'string' && effort.trim()) return effort.trim();
  return undefined;
}

/**
 * Stable OpenAI `prompt_cache_key` derived from the request's cacheable prefix
 * (top-level system prompt + tool definitions). OpenAI caches prompt prefixes
 * automatically; this key routes requests that share that prefix to the same
 * cache partition, raising hit rate — important in server mode where many
 * concurrent Claude Code sessions share one relay process.
 *
 * Keyed only on the STABLE prefix: within one Claude Code session every turn
 * sends byte-identical system+tools → same key → warm routing, while distinct
 * sessions (a different date/cwd baked into the system prompt) get distinct
 * keys, which is correct since they share no cacheable prefix. Deliberately
 * excludes folded inline system-reminders — those carry per-request-volatile
 * content (fresh timestamps, injected context) that would churn the key every
 * turn and defeat grouping.
 */
export function openAiPromptCacheKey(
  system: string | undefined,
  tools: AnthropicTool[] | undefined,
): string {
  const toolSig = (tools ?? [])
    .map(t => `${t.name}\x01${t.description ?? ''}\x01${JSON.stringify(t.input_schema ?? {})}`)
    .join('\x02');
  const material = `${system ?? ''}\0${toolSig}`;
  return 'relay-' + createHash('sha256').update(material).digest('hex').slice(0, 32);
}

/** Public OpenAI models that implement explicit prompt-cache breakpoints. */
export function supportsOpenAiPromptCacheBreakpoints(modelId: string): boolean {
  const match = modelId.toLowerCase().match(/^gpt-(\d+)(?:\.(\d+))?(?:-|$)/);
  if (!match) return false;
  const major = Number(match[1]);
  const minor = Number(match[2] ?? 0);
  return major > 5 || (major === 5 && minor >= 6);
}

export interface SdkCallParams {
  instructions?: string;
  messages: ModelMessage[];
  allowSystemInMessages?: boolean;
  tools?: Record<string, ReturnType<typeof tool>>;
  toolChoice?: 'auto' | 'none' | 'required' | { type: 'tool'; toolName: string };
  maxOutputTokens?: number;
  temperature?: number;
  providerOptions?: Record<string, Record<string, unknown>>;
  /** Per-request upstream headers; `streamText`/`generateText` take them as-is. */
  headers?: Record<string, string>;
}

// ── system ───────────────────────────────────────────────────────────────────
function stripClaudeCodeBillingHeader(text: string): string | undefined {
  if (!text.startsWith(CLAUDE_CODE_BILLING_HEADER_PREFIX)) return text;
  const newline = text.indexOf('\n');
  return newline === -1 ? undefined : text.slice(newline + 1);
}

function systemToString(
  system: AnthropicRequest['system'],
  stripAnthropicBillingHeader = false,
): string | undefined {
  if (!system) return undefined;
  if (typeof system === 'string') {
    return stripAnthropicBillingHeader ? stripClaudeCodeBillingHeader(system) : system;
  }
  const blocks = system.map(b => (typeof b === 'string' ? b : b.text ?? ''));
  if (!stripAnthropicBillingHeader) return blocks.join('\n');
  return blocks.flatMap(text => {
    const stripped = stripClaudeCodeBillingHeader(text);
    return stripped === undefined ? [] : [stripped];
  }).join('\n');
}

function openAiCacheBreakpoint(block: AnthropicBlock, enabled: boolean): Record<string, unknown> | undefined {
  if (!enabled || !block.cache_control) return undefined;
  return { openai: { promptCacheBreakpoint: { mode: 'explicit' } } };
}

function translateTopLevelSystemForOpenAi(
  system: AnthropicRequest['system'],
): ModelMessage[] {
  if (!system) return [];
  if (typeof system === 'string') {
    const stripped = stripClaudeCodeBillingHeader(system);
    return stripped?.trim() ? [{ role: 'system', content: stripped } as ModelMessage] : [];
  }
  return system.flatMap(block => {
    const raw = typeof block === 'string' ? block : block.text ?? '';
    const text = stripClaudeCodeBillingHeader(raw) ?? '';
    if (!text.trim()) return [];
    const cacheControl = typeof block === 'string' ? undefined : block.cache_control;
    return [{
      role: 'system',
      content: text,
      ...(cacheControl
        ? { providerOptions: { openai: { promptCacheBreakpoint: { mode: 'explicit' } } } }
        : {}),
    } as unknown as ModelMessage];
  });
}

// ── images ───────────────────────────────────────────────────────────────────
function imagePart(block: AnthropicBlock): {
  type: 'file';
  data: { type: 'data'; data: Uint8Array } | { type: 'url'; url: URL };
  mediaType: string;
} | null {
  const src = block.source;
  if (!src) return null;
  if (src.type === 'base64' && src.data) {
    return {
      type: 'file',
      data: { type: 'data', data: Buffer.from(src.data, 'base64') },
      mediaType: src.media_type ?? 'image',
    };
  }
  if (src.type === 'url' && src.url) {
    return {
      type: 'file',
      data: { type: 'url', url: new URL(src.url) },
      mediaType: src.media_type ?? 'image',
    };
  }
  return null;
}

/**
 * Serialize a tool_result for the text-only function-output channel, lifting
 * image blocks out into user-message parts (the caller pushes them right after
 * the tool message). Left inline, an image's base64 payload would be
 * JSON.stringify'd into the output text and tokenized as text at ~1.5 chars
 * per token — a single screenshot can cost 200k+ tokens upstream.
 */
function serializeToolResultForModel(
  tr: AnthropicBlock,
  imageParts: Array<Record<string, unknown>>,
): string {
  if (!Array.isArray(tr.content)) return serializeToolResultContent(tr.content);
  const rawId = splitToolUseId(tr.tool_use_id ?? '').rawId;
  let imageIndex = 0;
  const blocks = (tr.content as AnthropicBlock[]).map(block => {
    if (!block || block.type !== 'image') return block;
    const part = imagePart(block);
    if (!part) return block;
    imageIndex += 1;
    const label = `image ${imageIndex} of tool call ${rawId}`;
    imageParts.push({ type: 'text', text: `The following image is ${label}:` }, part);
    return { type: 'image', note: `attached to the next user message as ${label}` };
  });
  return JSON.stringify(blocks);
}

// ── tool_result name resolution (tool messages need the tool name) ────────────
export function annotateToolNames(messages: AnthropicMsg[]): void {
  const nameById = new Map<string, string>();
  for (const msg of messages) {
    if (!Array.isArray(msg.content)) continue;
    for (const b of msg.content) {
      if (b.type === 'tool_use' && b.id && b.name) nameById.set(splitToolUseId(b.id).rawId, b.name);
    }
  }
  for (const msg of messages) {
    if (!Array.isArray(msg.content)) continue;
    for (const b of msg.content) {
      if (b.type === 'tool_result' && b.tool_use_id) {
        b._name = nameById.get(splitToolUseId(b.tool_use_id).rawId);
      }
    }
  }
}

function thinkingToSdkPart(
  block: AnthropicBlock,
  npm: string,
): Record<string, unknown> | null {
  const text = block.thinking ?? '';
  if (npm === '@ai-sdk/openai' && !block.signature && !text.trim()) return null;

  const part: Record<string, unknown> = { type: 'reasoning', text };
  if (block.signature) {
    if (npm === '@ai-sdk/google') {
      part.providerOptions = { google: { thoughtSignature: block.signature } };
    } else if (npm === '@ai-sdk/openai' || npm === '@ai-sdk/openai-compatible') {
      part.providerOptions = { openai: { reasoningEncryptedContent: block.signature } };
    }
  }
  return part;
}

// ── messages: Anthropic → SDK ModelMessage[] ─────────────────────────────────
export function translateMessages(
  messages: AnthropicMsg[],
  npm: string,
  openAiPromptCacheBreakpoints = false,
): ModelMessage[] {
  const isGoogle = npm === '@ai-sdk/google';
  const out: ModelMessage[] = [];

  for (const msg of messages) {
    const blocks: AnthropicBlock[] = typeof msg.content === 'string'
      ? [{ type: 'text', text: msg.content }]
      : msg.content ?? [];

    if (msg.role === 'system') {
      // Claude Code deliberately injects trusted system messages within the
      // conversation. Preserve their position instead of moving volatile
      // reminders ahead of the stable history and invalidating the whole cache.
      for (const block of blocks) {
        if (block.type !== 'text' || !block.text?.trim()) continue;
        out.push({
          role: 'system',
          content: block.text,
          ...(openAiCacheBreakpoint(block, openAiPromptCacheBreakpoints)
            ? { providerOptions: openAiCacheBreakpoint(block, openAiPromptCacheBreakpoints) }
            : {}),
        } as unknown as ModelMessage);
      }
    } else if (msg.role === 'user') {
      const toolResults = blocks.filter(b => b.type === 'tool_result');
      const parts: Array<Record<string, unknown>> = [];
      for (const b of blocks) {
        if (b.type === 'text') {
          parts.push({
            type: 'text',
            text: b.text ?? '',
            ...(openAiCacheBreakpoint(b, openAiPromptCacheBreakpoints)
              ? { providerOptions: openAiCacheBreakpoint(b, openAiPromptCacheBreakpoints) }
              : {}),
          });
        } else if (b.type === 'image') {
          const p = imagePart(b);
          if (p) {
            parts.push({
              ...p,
              ...(openAiCacheBreakpoint(b, openAiPromptCacheBreakpoints)
                ? { providerOptions: openAiCacheBreakpoint(b, openAiPromptCacheBreakpoints) }
                : {}),
            });
          }
        }
      }
      const toolResultImageParts: Array<Record<string, unknown>> = [];
      if (toolResults.length) {
        out.push({
          role: 'tool',
          content: toolResults.map(tr => ({
            type: 'tool-result',
            toolCallId: splitToolUseId(tr.tool_use_id ?? '').rawId,
            toolName: tr._name ?? 'unknown',
            output: { type: 'text', value: serializeToolResultForModel(tr, toolResultImageParts) },
            ...(openAiCacheBreakpoint(tr, openAiPromptCacheBreakpoints)
              ? { providerOptions: openAiCacheBreakpoint(tr, openAiPromptCacheBreakpoints) }
              : {}),
          })),
        } as unknown as ModelMessage);
      }
      const userParts = [...toolResultImageParts, ...parts];
      if (userParts.length) out.push({ role: 'user', content: userParts } as unknown as ModelMessage);
    } else if (msg.role === 'assistant') {
      const parts: Array<Record<string, unknown>> = [];
      for (const b of blocks) {
        if (b.type === 'text') {
          // The OpenAI Responses API currently accepts breakpoints on input
          // content, not prior assistant output_text items.
          parts.push({ type: 'text', text: b.text ?? '' });
        } else if (b.type === 'thinking') {
          const restored = restoreOpenAiThinking(b.thinking ?? '', b.signature, npm);
          if (restored) parts.push(...restored);
          else {
            const part = thinkingToSdkPart(b, npm);
            if (part) parts.push(part);
          }
        } else if (b.type === 'tool_use' && b.id) {
          const { rawId, thoughtSignature } = splitToolUseId(b.id);
          const part: Record<string, unknown> = {
            type: 'tool-call', toolCallId: rawId, toolName: b.name, input: b.input ?? {},
          };
          if (thoughtSignature && isGoogle) part.providerOptions = { google: { thoughtSignature } };
          parts.push(part);
        }
      }
      if (parts.length) out.push({ role: 'assistant', content: parts } as unknown as ModelMessage);
    }
  }
  return out;
}

/** True for a JSON object — the only shape the tool-input strip rule applies to. */
function isPlainObject(value: unknown): boolean {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** True when `text` is parseable JSON, so forwarding it verbatim would not surface an error. */
function parsesAsJson(text: string): boolean {
  if (text.trim() === '') return true;
  try { JSON.parse(text); return true; } catch { return false; }
}

/**
 * Claude Code's non-streaming fallback rejects a `tool_use` whose `input` is
 * neither a string nor an object (lodash `isObject`, so arrays pass but numbers
 * and booleans do not) by throwing rather than answering with a tool_result the
 * model can retry. A scalar is never valid Anthropic tool input, so send the
 * empty object it used to collapse to and let validation reject it the ordinary
 * way. A string is kept: that path re-parses it, and an unparseable one becomes
 * the same retryable JSON-parse error the streamed path now produces.
 */
function representableToolInput(input: unknown): unknown {
  if (typeof input === 'string') return input;
  return isPlainObject(input) || Array.isArray(input) ? input : {};
}

/** Per-tool `required` property sets, read back out of the translated tool schemas. */
function toolRequiredProps(tools?: SdkCallParams['tools']): Map<string, ReadonlySet<string>> {
  const map = new Map<string, ReadonlySet<string>>();
  for (const [name, t] of Object.entries(tools ?? {})) {
    const schema = (t as { inputSchema?: { jsonSchema?: { required?: unknown } } }).inputSchema?.jsonSchema;
    const required = Array.isArray(schema?.required) ? schema.required : [];
    map.set(name, new Set(required.filter((r): r is string => typeof r === 'string')));
  }
  return map;
}

export function translateTools(anthropicTools?: AnthropicTool[], npm?: string): Record<string, ReturnType<typeof tool>> | undefined {
  if (!anthropicTools?.length) return undefined;
  // Anthropic-format routes take Claude Code's ECMAScript patterns as written;
  // every other provider validates them in a dialect that may not compile them.
  const anthropicFormat = npm === '@ai-sdk/anthropic' || npm === VERTEX_ANTHROPIC_NPM;
  const tools: Record<string, ReturnType<typeof tool>> = {};
  for (const t of anthropicTools) {
    if (!t.name || !t.input_schema) continue;
    tools[t.name] = tool({
      description: t.description ?? '',
      inputSchema: jsonSchema(anthropicFormat ? t.input_schema : sanitizeToolSchema(t.input_schema)),
      strict: npm === '@ai-sdk/openai' ? false : undefined,
    });
  }
  return Object.keys(tools).length ? tools : undefined;
}

export function translateToolChoice(tc: AnthropicRequest['tool_choice']): SdkCallParams['toolChoice'] {
  if (!tc) return undefined;
  if (tc.type === 'auto') return 'auto';
  if (tc.type === 'any') return 'required';
  if (tc.type === 'tool' && tc.name) return { type: 'tool', toolName: tc.name };
  return undefined;
}

const {
  start: COMPACT_TEXT_ONLY_START,
  end: COMPACT_TEXT_ONLY_END,
} = CLAUDE_CODE_COMPACT_PROMPT_MARKERS;

/**
 * Claude Code forks its reactive-compaction turn with the SAME tool definitions
 * as an ordinary turn and relies on the prompt alone — "Respond with TEXT ONLY"
 * — to stop the model calling them. OpenAI-family models ignore that and answer
 * with whichever tool the conversation made salient: StructuredOutput in a
 * schema-mode agent, but Bash after a shell-heavy session, and in principle any
 * tool at all. The fork denies tool EXECUTION and allows one turn, so a call it
 * emits buys nothing: it burns the turn and returns no summary text. The
 * reactive path has no second chance at all; the manual path retries once
 * outside the fork with a reduced tool set, and then gives up too. Claude
 * Code discards the attempt, and three consecutive failures open a circuit
 * breaker that short-circuits every later AUTOMATIC compaction — with no API
 * call — until the counter is reset by a successful compaction or a fresh query
 * invocation. A headless or subagent run is a single invocation, so there it
 * never resets: the conversation grows until it dies on Claude Code's own
 * "Prompt is too long" guard.
 *
 * So key on the compact envelope only — the marker text is what identifies this
 * turn, never the tool list, which is why the earlier StructuredOutput
 * precondition was too narrow. If Claude Code changes the envelope, this
 * deliberately fails open rather than stripping tools from an ordinary request.
 *
 * The envelope must OPEN a text block, not merely appear in one. Every builder
 * puts the header first and appends the reminder to the same string, so an
 * anchored match costs nothing; an unanchored one fires on any turn that merely
 * quotes the envelope — a pasted prompt, a subagent's report, a read of this
 * very file — and silently takes tools away from a turn that needed them.
 */
function isClaudeCodeCompactRequest(body: AnthropicRequest): boolean {
  if (body.diagnostics !== undefined) return false;

  const finalMessage = body.messages.at(-1);
  if (!finalMessage || finalMessage.role !== 'user') return false;
  const texts = typeof finalMessage.content === 'string'
    ? [finalMessage.content]
    : finalMessage.content
      .filter(block => block.type === 'text')
      .map(block => block.text ?? '');
  return texts.some(text =>
    text.startsWith(COMPACT_TEXT_ONLY_START) && text.includes(COMPACT_TEXT_ONLY_END));
}

// This sentence is the reason the compaction fork needs a text-only response,
// not decoration around it. It occurs in both prompt builders across 27 extracted
// 2.1.238–2.1.260 bundles; all eight platforms are represented for 2.1.257 and
// 2.1.260. The recognizer deliberately covers only rewordings that preserve this
// anchor and a bounded opening grammar. The imperative must start at byte zero
// (after an optional known severity label), so quoted copies with a preamble stay
// out; a raw copy pasted with no preamble is indistinguishable from Claude Code's
// own prompt. The START guard excludes known partial envelopes. A
// START-intact/END-reworded prompt also stays invisible by design because it is
// indistinguishable from a user quoting the header; the per-build release probe
// catches removal or in-place rewording of either current marker.
const COMPACT_DRIFT_ANCHOR = 'Tool calls will be REJECTED and will waste your only turn';
const COMPACT_DRIFT_ANCHOR_LINE = new RegExp(
  `(?:^|\\n)[-*•\\s]{0,4}${COMPACT_DRIFT_ANCHOR}`,
);
const COMPACT_DRIFT_OPENING = /^(?:(?:critical|important|warning|caution|urgent|notice):\s*)?(?:respond|return|answer|output|write|provide)\b(?=[^\n]{1,160}(?:\n|$))(?=[^\n]*\b(?:text\s+only|plain\s+text\s+only|only\s+(?:plain\s+)?text)\b)(?=[^\n]*\b(?:do not|don't|never|without)\b[^\n]{0,48}\btools?\b)/i;

function looksLikeDriftedClaudeCodeCompactRequest(body: AnthropicRequest): boolean {
  if (body.diagnostics !== undefined) return false;

  const finalMessage = body.messages.at(-1);
  if (!finalMessage || finalMessage.role !== 'user') return false;
  const texts = typeof finalMessage.content === 'string'
    ? [finalMessage.content]
    : finalMessage.content
      .filter(block => block.type === 'text')
      .map(block => block.text ?? '');
  return texts.some(text =>
    !text.startsWith(COMPACT_TEXT_ONLY_START)
    && COMPACT_DRIFT_OPENING.test(text)
    && COMPACT_DRIFT_ANCHOR_LINE.test(text));
}

function claudeCodeVersionFromRequest(body: AnthropicRequest): string | undefined {
  const texts = typeof body.system === 'string'
    ? [body.system]
    : (body.system ?? []).map(block => typeof block === 'string' ? block : block.text ?? '');
  for (const text of texts) {
    if (!text.startsWith(CLAUDE_CODE_BILLING_HEADER_PREFIX)) continue;
    const match = text.match(/\bcc_version=([0-9A-Za-z][0-9A-Za-z._+-]{0,63})(?:;|\s|$)/);
    if (match) return match[1];
  }
  return undefined;
}

const warnedCompactPromptDrifts = new Set<string>();
const MAX_COMPACT_PROMPT_DRIFT_WARNINGS = 3;

function reportClaudeCodeCompactPromptDrift(
  body: AnthropicRequest,
  log?: (message: string) => void,
): void {
  if (!looksLikeDriftedClaudeCodeCompactRequest(body)) return;
  const version = claudeCodeVersionFromRequest(body);
  const signature = version ?? 'unknown-version';
  try { log?.(`possible Claude Code compact prompt drift: ${signature}`); } catch { /* ignore */ }
  if (warnedCompactPromptDrifts.has(signature)) return;
  if (warnedCompactPromptDrifts.size >= MAX_COMPACT_PROMPT_DRIFT_WARNINGS) return;
  warnedCompactPromptDrifts.add(signature);
  const versionText = version ? ` from Claude Code ${version}` : '';
  // emitParentNotice, not a bare process.stderr.write: while `clodex claude` has
  // Claude Code running, launch.ts mutes the parent's stderr to protect the TUI.
  emitParentNotice(
    `clodex: warning: a request${versionText} looks like a compaction turn, but its prompt no longer `
      + "matches clodex's text-only guard. Tools were left enabled and compaction may fail. Please "
      + 'report this at https://github.com/bman654/clodex/issues',
  );
  if (warnedCompactPromptDrifts.size === MAX_COMPACT_PROMPT_DRIFT_WARNINGS) {
    emitParentNotice('clodex: warning: further compact-prompt drift warnings suppressed.');
  }
}

/** Test seam: the warning cap is process-wide and would leak between cases. */
export function resetCompactPromptDriftWarningsForTests(): void {
  warnedCompactPromptDrifts.clear();
}

export function translateRequest(
  body: AnthropicRequest,
  npm: string,
  options?: TranslateRequestOptions,
): SdkCallParams {
  const messages = body.messages ?? [];
  annotateToolNames(messages);

  // Claude Code prepends an Anthropic-only billing attribution block that can
  // carry a changing `cc_prev_req` value on first-party-base-URL requests. It
  // is envelope metadata, not a model instruction, and forwarding it
  // invalidates the stable prompt prefix on every SDK-translated route —
  // OpenAI and OpenAI-compatible providers hash the request prefix for implicit
  // caching, so a volatile first system line caps their cache hits at the first
  // few hundred tokens (observed live: kimi-k3 via OpenCode Zen froze at
  // 256-512 cached tokens per call). Strip it on every SDK-translated route;
  // Anthropic passthrough does not go through translateRequest and still
  // forwards it.
  const baseSystem = systemToString(body.system, true);
  const systemText = baseSystem?.trim() || (options?.openAiOAuth ? 'You are a coding assistant.' : undefined);

  // resolveUpstreamTools uses the shared proxy types; the adapter keeps its own
  // minimal request shapes, so cast at this boundary. Keep compact-request tool
  // definitions intact for prompt-cache prefix reuse; toolChoice='none' below
  // makes them unavailable at the provider API rather than by prompt compliance.
  const compactRequest = isClaudeCodeCompactRequest(body);
  if (!compactRequest) reportClaudeCodeCompactPromptDrift(body, options?.log);
  let upstreamTools = resolveUpstreamTools(
    body.tools as unknown as AnthropicToolDefinition[] | undefined,
    messages as unknown as AnthropicRequestMessage[],
  ) as unknown as AnthropicTool[];
  if (options?.maxTools !== undefined && upstreamTools.length > options.maxTools) {
    upstreamTools = upstreamTools.slice(0, options.maxTools);
  }
  const effort = anthropicEffortFromRequest(body) ?? options?.defaultEffort;
  let providerOptions = deepMergeProviderOptions(
    thinkingProviderOptions(npm),
    effortProviderOptions(npm, effort, options?.reasoningMetadata?.upstreamModelId ?? body.model, options?.reasoningMetadata),
  );

  // ChatGPT Codex OAuth backend requires `instructions` in providerOptions and
  // rejects the standard `system` field. It also manages its own output limit.
  if (options?.openAiOAuth && systemText) {
    providerOptions = deepMergeProviderOptions(providerOptions, {
      openai: { instructions: systemText },
    });
  }

  const upstreamModelId = options?.reasoningMetadata?.upstreamModelId ?? body.model;
  const supportsExplicitOpenAiCaching = !options?.openAiOAuth
    && supportsOpenAiPromptCacheBreakpoints(upstreamModelId);

  // Keep related requests in one cache partition. Prefer Claude Code's stable
  // session identity when available; the system/tools hash remains the fallback
  // for other Anthropic clients and API-server callers.
  //
  // GPT-5.6+ public-API implicit mode also
  // honors the explicit breakpoints copied from Claude Code's cache_control
  // blocks, while retaining an automatic latest-message breakpoint as fallback.
  if (npm === '@ai-sdk/openai') {
    const claudeSessionId = extractClaudeSessionId(body, options?.claudeSessionId);
    const serviceTier = options?.openAiOAuth ? oauthServiceTier() : undefined;
    providerOptions = deepMergeProviderOptions(providerOptions, {
      openai: {
        promptCacheKey: claudeSessionId
          ? claudeSessionPromptCacheKey(claudeSessionId)
          : openAiPromptCacheKey(baseSystem, upstreamTools),
        ...(supportsExplicitOpenAiCaching
          ? { promptCacheOptions: { mode: 'implicit', ttl: '30m' } }
          : {}),
        ...(serviceTier ? { serviceTier } : {}),
      },
    });
  }

  return {
    instructions: options?.openAiOAuth || supportsExplicitOpenAiCaching ? undefined : systemText,
    messages: [
      ...(supportsExplicitOpenAiCaching ? translateTopLevelSystemForOpenAi(body.system) : []),
      ...translateMessages(messages, npm, supportsExplicitOpenAiCaching),
    ],
    allowSystemInMessages: true,
    tools: translateTools(upstreamTools.length ? upstreamTools : undefined, npm),
    toolChoice: compactRequest ? 'none' : translateToolChoice(body.tool_choice),
    maxOutputTokens: options?.openAiOAuth ? undefined : body.max_tokens,
    temperature: body.temperature,
    providerOptions,
  };
}

/**
 * Service tier for ChatGPT-OAuth (Codex backend) requests — Codex "fast mode"
 * (Codex CLI config `service_tier = "fast"`; wire value `priority`). Applied
 * ONLY on the OAuth route, and only after alias/remap resolution, so an alias
 * that resolves to a ChatGPT model gets the tier while the same worker slot
 * remapped to a non-OpenAI provider never sends it. API-key OpenAI is
 * deliberately excluded: on the public API `priority` is a billable per-token
 * surcharge, not a plan feature. Absence preserves the backend default exactly.
 */
/**
 * Whether a route is the ChatGPT-OAuth (Codex) backend — the only one that
 * carries a service tier.
 *
 * Exported so the request diagnostic reports the tier for exactly the routes
 * the adapter applies it to. Recomputing the predicate at each site is how a
 * log grows into a confident lie about what went on the wire.
 */
export function isOpenAiOAuthRoute(
  route: { npm?: string; authType?: string } | undefined,
): boolean {
  return route?.npm === '@ai-sdk/openai' && route.authType === 'oauth';
}

const SERVICE_TIERS = new Set(['auto', 'default', 'flex', 'priority']);
let warnedInvalidServiceTier = false;
let warnedUnsupportedServiceTier = false;

export function oauthServiceTier(): string | undefined {
  const raw = process.env.CLODEX_SERVICE_TIER;
  if (raw === undefined || raw.trim() === '') return undefined;
  const normalized = raw.trim().toLowerCase() === 'fast' ? 'priority' : raw.trim().toLowerCase();
  if (!SERVICE_TIERS.has(normalized)) {
    if (!warnedInvalidServiceTier) {
      warnedInvalidServiceTier = true;
      // emitParentNotice, not console.error: this fires from a live request, and
      // `clodex claude` has the parent's stdio muted for Claude Code's TUI.
      emitParentNotice('clodex: ignoring CLODEX_SERVICE_TIER (expected auto, default, flex, priority, or fast)');
    }
    return undefined;
  }
  return normalized;
}

export function reportUnsupportedServiceTier(params: SdkCallParams, warnings: unknown): void {
  if (warnedUnsupportedServiceTier || !params.providerOptions?.openai?.serviceTier) return;
  if (!Array.isArray(warnings) || !warnings.some(warning => {
    if (!warning || typeof warning !== 'object') return false;
    const candidate = warning as { type?: unknown; feature?: unknown };
    return candidate.type === 'unsupported' && candidate.feature === 'serviceTier';
  })) return;
  warnedUnsupportedServiceTier = true;
  emitParentNotice('clodex: requested service tier was not sent for this model; the backend default will be used');
}

export function resetServiceTierWarningForTests(): void {
  warnedInvalidServiceTier = false;
  warnedUnsupportedServiceTier = false;
}

// ── usage: SDK → Anthropic ────────────────────────────────────────────────────
interface SdkUsage {
  inputTokens?: number;
  outputTokens?: number;
  inputTokenDetails?: { cacheReadTokens?: number; cacheWriteTokens?: number };
  /** AI SDK 6 compatibility for older third-party LanguageModel implementations. */
  cachedInputTokens?: number;
}
interface AnthropicUsage {
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
}

/** Hand the provider's own prompt total to the observer, when it reported one. */
function reportPromptTokens(
  observer: AnthropicStreamObserver | undefined,
  usage: SdkUsage | undefined,
): void {
  const total = usage?.inputTokens;
  if (observer?.onPromptTokens && typeof total === 'number' && Number.isFinite(total)) {
    observer.onPromptTokens(total);
  }
}

/**
 * Map SDK usage → Anthropic usage. SDK providers report the cache-hit subset in
 * `inputTokenDetails`, counted WITHIN the prompt total. The Anthropic schema
 * expects cache reads and writes in separate fields, so subtract both subsets
 * from input_tokens to avoid double-counting. GPT-5.6+ reports cache writes;
 * older models generally report reads only.
 */
function toAnthropicUsage(u?: SdkUsage): AnthropicUsage {
  const total = u?.inputTokens ?? 0;
  const cacheRead = u?.inputTokenDetails?.cacheReadTokens ?? u?.cachedInputTokens ?? 0;
  const cacheWrite = u?.inputTokenDetails?.cacheWriteTokens ?? 0;
  return {
    input_tokens: Math.max(0, total - cacheRead - cacheWrite),
    output_tokens: u?.outputTokens ?? 0,
    cache_creation_input_tokens: cacheWrite,
    cache_read_input_tokens: cacheRead,
  };
}

// ── response: SDK fullStream → Anthropic SSE ─────────────────────────────────
type WriteFn = (chunk: string) => void;

type LogFn = (msg: () => string) => void;

export interface AnthropicStreamObserver {
  /** Called for every AI SDK fullStream part before Relay translates it. */
  onPart?: (partType: string) => void;
  /**
   * Total prompt tokens the provider counted, cached portion included. Reported
   * before the Anthropic split, because provider pricing bands apply to the whole
   * input rather than the uncached remainder.
   */
  onPromptTokens?: (total: number) => void;
  /** Local fallback used when the provider omits usage at stream completion. */
  initialInputTokens?: number;
  abortSignal?: AbortSignal;
  /** Abort if the provider produces no stream event for this long. */
  idleTimeoutMs?: number;
}

function streamAbortError(signal?: AbortSignal): Error {
  if (signal?.reason instanceof Error) return signal.reason;
  const error = new Error(
    typeof signal?.reason === 'string' ? signal.reason : 'SDK stream aborted',
  );
  error.name = 'AbortError';
  return error;
}

/**
 * Forward caller cancellation into a Relay-owned controller without creating
 * an AbortSignal.any() composite. Node 24 retains source-aborted composite
 * signals in its internal gcPersistentSignals set when listeners remain.
 */
export function forwardAbortSignal(source: AbortSignal | undefined, target: AbortController): () => void {
  if (!source) return () => {};
  const forward = () => {
    if (!target.signal.aborted) target.abort(source.reason);
  };
  if (source.aborted) {
    forward();
    return () => {};
  }
  source.addEventListener('abort', forward, { once: true });
  return () => source.removeEventListener('abort', forward);
}

/**
 * The id clodex gives a message it translated from another provider.
 *
 * It must NOT start with `msg_`. Claude Code (the gate is in every build from
 * 2.1.268 on, and fires in proxy mode when its thread rollout is enabled)
 * treats an assistant message as an anchor for server-side thread
 * continuation when its id starts with `msg_`, or when the response carried a
 * `request-id` header; translated responses never send that header, so the id
 * alone decides. An anchored follow-up carries
 * `thread:{type:"continue",previous_message_id}` and only the messages after
 * the anchor, trusting the server to hold the rest. No translated upstream
 * holds that state and clodex does not reconstruct it, so the delta reached
 * the provider as a bare tool result (`No function call found for function
 * call output`), and Claude Code retried each such request with the full
 * history. An id outside the anchor prefix makes Claude Code send the full
 * history in the first place, which is what these routes are built for.
 */
function translatedMessageId(): string {
  return 'clodex_' + randomUUID().replace(/-/g, '');
}

export async function writeAnthropicStream(
  stream: AsyncIterable<FullStreamPart>,
  modelId: string,
  write: WriteFn,
  log?: LogFn,
  observer?: AnthropicStreamObserver,
  tools?: SdkCallParams['tools'],
): Promise<void> {
  const messageId = translatedMessageId();
  const requiredProps = toolRequiredProps(tools);
  let blockIndex = -1;
  let started = false;
  let openType: 'text' | 'thinking' | 'tool' | null = null;
  let pendingThinkingSig: string | undefined;
  let openAiThinking: OpenAiThinkingBlock | undefined;
  const idToBlock = new Map<string, number>();
  // Tool input deltas are buffered (not forwarded raw) so the complete input
  // can be sanitized once the SDK's parsed `tool-call` part arrives.
  const toolJsonBuffer = new Map<string, string>();
  const flushedTools = new Set<string>();
  let openToolId: string | null = null;
  let finishReason = 'end_turn';
  let usage: AnthropicUsage = {
    input_tokens: observer?.initialInputTokens ?? 0,
    output_tokens: 0,
    cache_creation_input_tokens: 0,
    cache_read_input_tokens: 0,
  };

  const emit = (event: string, data: unknown) => write(sseChunk(event, data));
  const ensureStart = () => {
    if (started) return;
    emit('message_start', {
      type: 'message_start',
      message: {
        id: messageId, type: 'message', role: 'assistant', content: [],
        model: modelId, stop_reason: null, stop_sequence: null,
        usage: {
          input_tokens: 0,
          output_tokens: 0,
          cache_creation_input_tokens: 0,
          cache_read_input_tokens: 0,
        },
      },
    });
    started = true;
  };
  const closeOpen = () => {
    if (openType === 'thinking') {
      // Emit the complete signature once: Claude Code replaces, rather than
      // appends, signature_delta values. Splitting an envelope would lose it.
      emit('content_block_delta', {
        type: 'content_block_delta', index: blockIndex,
        delta: { type: 'signature_delta', signature: openAiThinking?.signature() ?? pendingThinkingSig ?? '' },
      });
      pendingThinkingSig = undefined;
      openAiThinking = undefined;
    }
    // Stream ended (or moved on) without a tool-call part for this block: emit
    // the buffered raw JSON so the deltas that did arrive are not lost.
    if (openType === 'tool' && openToolId !== null && !flushedTools.has(openToolId)) {
      const buffered = toolJsonBuffer.get(openToolId);
      if (buffered) {
        emit('content_block_delta', {
          type: 'content_block_delta', index: blockIndex,
          delta: { type: 'input_json_delta', partial_json: buffered },
        });
      }
      flushedTools.add(openToolId);
    }
    if (openType) emit('content_block_stop', { type: 'content_block_stop', index: blockIndex });
    openType = null;
    openToolId = null;
  };
  const openBlock = (type: 'text' | 'thinking' | 'tool', contentBlock: unknown) => {
    ensureStart(); closeOpen(); blockIndex++; openType = type;
    emit('content_block_start', { type: 'content_block_start', index: blockIndex, content_block: contentBlock });
  };

  for await (const part of stream) {
    observer?.onPart?.(part.type);
    if (observer?.abortSignal?.aborted) throw streamAbortError(observer.abortSignal);
    switch (part.type) {
      // The SDK emits start before it knows whether the provider accepted the
      // request. Wait for content/finish so a pre-content HTTP failure can still
      // propagate through the proxy with its real non-2xx status.
      case 'start': break;

      // An abort is terminal but is not an error part in the AI SDK stream. If
      // treated like an unknown part, the loop ends and Relay synthesizes a
      // message_start/message_delta/message_stop after the client disconnected.
      // Throw so the HTTP layer follows its cancellation path and emits nothing.
      case 'abort':
        throw streamAbortError(observer?.abortSignal);

      case 'reasoning-start':
        // Consecutive OpenAI summaries/items share a live block, so a thinking-only
        // transport drop has no completed block to prevent Claude Code's retry.
        // The signature records their identities and boundaries for lossless replay.
        if (openAiReasoningItemId(part) && part.id) {
          if (!openAiThinking) {
            openBlock('thinking', { type: 'thinking', thinking: '', signature: '' });
            openAiThinking = new OpenAiThinkingBlock();
          }
          openAiThinking.start(part);
        } else {
          openBlock('thinking', { type: 'thinking', thinking: '', signature: '' });
        }
        break;
      case 'reasoning-delta':
        if (openType !== 'thinking') openBlock('thinking', { type: 'thinking', thinking: '', signature: '' });
        emit('content_block_delta', {
          type: 'content_block_delta', index: blockIndex,
          delta: { type: 'thinking_delta', thinking: openAiThinking ? openAiThinking.append(part) : part.text ?? '' },
        });
        break;
      case 'reasoning-end': {
        if (openAiThinking) openAiThinking.end(part);
        else {
          const sig = grabRoundTripSignature(part);
          if (sig) pendingThinkingSig = sig;
        }
        break;
      }

      case 'text-start':
        openBlock('text', { type: 'text', text: '' });
        break;
      case 'text-delta':
        if (openType !== 'text') openBlock('text', { type: 'text', text: '' });
        emit('content_block_delta', {
          type: 'content_block_delta', index: blockIndex,
          delta: { type: 'text_delta', text: part.text ?? '' },
        });
        break;
      case 'text-end': break;

      case 'tool-input-start': {
        const sig = grabRoundTripSignature(part);
        openBlock('tool', {
          type: 'tool_use', id: encodeToolUseId(part.id ?? '', sig), name: part.toolName, input: {},
        });
        idToBlock.set(part.id ?? '', blockIndex);
        openToolId = part.id ?? '';
        break;
      }
      case 'tool-input-delta': {
        const id = part.id ?? '';
        toolJsonBuffer.set(id, (toolJsonBuffer.get(id) ?? '') + (part.delta ?? part.text ?? ''));
        break;
      }
      case 'tool-input-end': break;

      case 'tool-call': {
        finishReason = 'tool_use';
        const id = part.toolCallId ?? '';
        if (idToBlock.has(id)) {
          // Streamed input: emit the sanitized complete input as one delta,
          // falling back to the buffered raw JSON if the SDK gave no parsed input.
          //
          // Arguments that do not parse at all are forwarded as the model's own
          // bytes. Claude Code re-parses this text, so anything derived from a
          // failed parse — the SDK's raw-string passthrough, JSON-quoted — parses
          // cleanly on its side and is spread into a character-index map that it
          // then acts on. Sending the bytes makes its parse fail as ours did, and
          // it answers the model with a retryable "could not be parsed as JSON".
          if (!flushedTools.has(id)) {
            const buffered = toolJsonBuffer.get(id);
            const json = buffered !== undefined && !isPlainObject(part.input) && !parsesAsJson(buffered)
              ? buffered
              : part.input !== undefined && part.input !== null
                ? JSON.stringify(sanitizeToolInput(part.input, requiredProps.get(part.toolName ?? '')))
                : (buffered ?? '');
            if (json) {
              emit('content_block_delta', {
                type: 'content_block_delta', index: idToBlock.get(id) ?? blockIndex,
                delta: { type: 'input_json_delta', partial_json: json },
              });
            }
            flushedTools.add(id);
          }
        } else if (openType !== 'tool') {
          // Non-streamed tool call (no input-start/delta arrived): emit a full block.
          const sig = grabRoundTripSignature(part);
          openBlock('tool', {
            type: 'tool_use', id: encodeToolUseId(id, sig), name: part.toolName, input: {},
          });
          emit('content_block_delta', {
            type: 'content_block_delta', index: blockIndex,
            delta: { type: 'input_json_delta', partial_json: JSON.stringify(sanitizeToolInput(part.input ?? {}, requiredProps.get(part.toolName ?? ''))) },
          });
          flushedTools.add(id);
        }
        break;
      }

      case 'finish':
        if (part.totalUsage) {
          reportPromptTokens(observer, part.totalUsage);
          const finalUsage = toAnthropicUsage(part.totalUsage);
          const hasFinalInputUsage = finalUsage.input_tokens
            + finalUsage.cache_creation_input_tokens
            + finalUsage.cache_read_input_tokens > 0;
          usage = hasFinalInputUsage
            ? finalUsage
            : { ...usage, output_tokens: finalUsage.output_tokens };
        }
        if (part.finishReason === 'tool-calls') finishReason = 'tool_use';
        else if (part.finishReason === 'length') finishReason = 'max_tokens';
        else if (part.finishReason === 'stop' && finishReason !== 'tool_use') finishReason = 'end_turn';
        break;

      case 'error': {
        const e = part.error as { data?: unknown; message?: string } | undefined;
        const errMsg = e?.message || (typeof part.error === 'string' ? part.error : JSON.stringify(e?.data ?? part.error));
        const transportCode = sdkUpstreamErrorDetails(part.error)?.transportCode;
        const errorType = anthropicErrorType(upstreamHttpStatus(part.error, errMsg), transportCode);
        log?.(() => `sdk stream error (${errorType}): ${errMsg}`);
        // Claude Code retries a mid-stream failure only while no content block
        // has completed. Closing a thinking block here would count as completed
        // content and turn a recoverable transport drop into a dead turn, so
        // leave it open; the client closes it itself when it retries. Text and
        // tool blocks stay closed: once visible output exists the client
        // finalizes the partial turn either way, and a tool block's buffered
        // arguments must still be flushed.
        if (!(transportCode === 'websocket_transport_error' && openType === 'thinking')) closeOpen();
        throw part.error instanceof Error || (part.error && typeof part.error === 'object')
          ? part.error
          : new Error(errMsg);
      }

      default: break;
    }
  }

  // Some SDK transports end the iterator without yielding an explicit abort
  // part. Never synthesize completion frames for an already-cancelled request.
  if (observer?.abortSignal?.aborted) throw streamAbortError(observer.abortSignal);

  closeOpen();
  ensureStart();
  emit('message_delta', { type: 'message_delta', delta: { stop_reason: finishReason, stop_sequence: null }, usage });
  emit('message_stop', { type: 'message_stop' });
}

// ── high-level entry points ──────────────────────────────────────────────────
export async function streamAnthropicResponse(
  model: LanguageModel,
  params: SdkCallParams,
  modelId: string,
  write: WriteFn,
  log?: LogFn,
  observer?: AnthropicStreamObserver,
): Promise<void> {
  const { idleTimeoutMs, totalTimeoutMs, maxRetries } = upstreamRequestBudget({
    idleTimeoutMs: observer?.idleTimeoutMs,
  });
  const attempts = trackUpstreamAttempts(model);
  const idleAbort = new AbortController();
  const stopForwardingAbort = forwardAbortSignal(observer?.abortSignal, idleAbort);
  const abortSignal = idleAbort.signal;
  const idleError = () => attempts.deadlineError(
    new Error(`no data received from provider for ${Math.round(idleTimeoutMs / 1000)}s`),
  );
  let idleTimer = setTimeout(() => idleAbort.abort(idleError()), idleTimeoutMs);
  const totalTimer = setTimeout(
    () => idleAbort.abort(attempts.deadlineError(
      new Error(`provider stream exceeded ${Math.round(totalTimeoutMs / 1000)}s`),
    )),
    totalTimeoutMs,
  );
  // Do not combine streamText's total/chunk timeout signals here. In AI SDK
  // 7.0.22 that composition retains completed StreamTextResult graphs. Relay
  // owns the timers and explicitly settles its controller after consumption.
  try {
    const result = streamText({
      model: attempts.model,
      ...params,
      maxRetries,
      abortSignal,
      onError: () => {},
      onStepFinish: step => reportUnsupportedServiceTier(params, step.warnings),
    } as Parameters<typeof streamText>[0]);

    const watchedStream = (async function* () {
      try {
        for await (const part of result.stream as AsyncIterable<FullStreamPart>) {
          clearTimeout(idleTimer);
          idleTimer = setTimeout(() => idleAbort.abort(idleError()), idleTimeoutMs);
          yield part;
        }
      } finally {
        clearTimeout(idleTimer);
      }
    })();

    await writeAnthropicStream(watchedStream, modelId, write, log, { ...observer, abortSignal }, params.tools);
  } finally {
    stopForwardingAbort();
    clearTimeout(idleTimer);
    clearTimeout(totalTimer);
    // Settle the direct Relay-owned signal only after stream consumption. Do not
    // replace this with AbortSignal.any(): source-driven abort leaves Node's
    // dependent composite rooted in gcPersistentSignals on Node 24.
    if (!idleAbort.signal.aborted) idleAbort.abort();
  }
}

export async function generateAnthropicResponse(
  model: LanguageModel,
  params: SdkCallParams,
  modelId: string,
  options?: {
    forceStream?: boolean;
    abortSignal?: AbortSignal;
    onPart?: (partType: string) => void;
    onPromptTokens?: (total: number) => void;
    idleTimeoutMs?: number;
  },
): Promise<Record<string, unknown>> {
  let text: string;
  let toolCalls: Array<{ toolCallId: string; toolName: string; input: unknown }>;
  let finishReason: string;
  let usage: SdkUsage | undefined;
  let warnings: unknown;
  const { idleTimeoutMs, totalTimeoutMs, maxRetries } = upstreamRequestBudget({
    idleTimeoutMs: options?.forceStream ? options.idleTimeoutMs : undefined,
  });
  const attempts = trackUpstreamAttempts(model);

  if (options?.forceStream) {
    // Some upstreams (e.g. ChatGPT's Codex backend) reject non-streaming requests
    // outright. Request a real stream from the SDK and collect it into one
    // response instead of forwarding the client's non-streaming request upstream.
    const forceAbort = new AbortController();
    const stopForwardingAbort = forwardAbortSignal(options.abortSignal, forceAbort);
    const abortSignal = forceAbort.signal;
    const idleError = () => attempts.deadlineError(
      new Error(`no data received from provider for ${Math.round(idleTimeoutMs / 1000)}s`),
    );
    let idleTimer = setTimeout(() => forceAbort.abort(idleError()), idleTimeoutMs);
    const totalTimer = setTimeout(
      () => forceAbort.abort(attempts.deadlineError(
        new Error(`provider stream exceeded ${Math.round(totalTimeoutMs / 1000)}s`),
      )),
      totalTimeoutMs,
    );
    // See the streaming path above: Relay owns these timers and explicitly
    // settles its controller when the stream has been fully reduced.
    const streamedText: string[] = [];
    const streamedToolCalls: Array<{ toolCallId: string; toolName: string; input: unknown }> = [];
    let streamedFinishReason = 'stop';
    let streamedUsage: SdkUsage | undefined;
    try {
      const r = streamText({
        model: attempts.model,
        ...params,
        maxRetries,
        abortSignal,
        onError: () => {},
        onStepFinish: step => reportUnsupportedServiceTier(params, step.warnings),
      } as Parameters<typeof streamText>[0]);
      for await (const part of r.stream as AsyncIterable<FullStreamPart>) {
        clearTimeout(idleTimer);
        idleTimer = setTimeout(() => forceAbort.abort(idleError()), idleTimeoutMs);
        options.onPart?.(part.type);
        if (abortSignal.aborted || part.type === 'abort') {
          throw streamAbortError(abortSignal);
        }
        if (part.type === 'error') {
          throw part.error instanceof Error || (part.error && typeof part.error === 'object')
            ? part.error
            : new Error(typeof part.error === 'string' ? part.error : 'Upstream stream failed');
        }
        if (part.type === 'text-delta') streamedText.push(part.text ?? '');
        else if (part.type === 'tool-call') {
          streamedToolCalls.push({
            toolCallId: part.toolCallId ?? '',
            toolName: part.toolName ?? '',
            input: part.input,
          });
        } else if (part.type === 'finish') {
          streamedFinishReason = part.finishReason ?? streamedFinishReason;
          streamedUsage = part.totalUsage;
        }
      }
      if (abortSignal.aborted) throw streamAbortError(abortSignal);
    } finally {
      stopForwardingAbort();
      clearTimeout(idleTimer);
      clearTimeout(totalTimer);
      // See the streaming path above: settle the Relay-owned signal after the
      // result is fully reduced so Node can release AI SDK's listener graph.
      if (!forceAbort.signal.aborted) forceAbort.abort();
    }
    text = streamedText.join('');
    toolCalls = streamedToolCalls;
    finishReason = streamedFinishReason;
    usage = streamedUsage;
  } else {
    // generateText exposes no intermediate events that could reset an idle
    // timer, so only the total provider-call deadline applies on this path.
    const generateAbort = new AbortController();
    const stopForwardingAbort = forwardAbortSignal(options?.abortSignal, generateAbort);
    const totalTimer = setTimeout(
      () => generateAbort.abort(attempts.deadlineError(
        new Error(`provider request exceeded ${Math.round(totalTimeoutMs / 1000)}s`),
      )),
      totalTimeoutMs,
    );
    try {
      const r = await generateText({
        model: attempts.model,
        ...params,
        maxRetries,
        abortSignal: generateAbort.signal,
      } as Parameters<typeof generateText>[0]);
      ({ text, toolCalls, finishReason, usage, warnings } = r);
    } catch (error) {
      if (generateAbort.signal.aborted) throw streamAbortError(generateAbort.signal);
      throw error;
    } finally {
      stopForwardingAbort();
      clearTimeout(totalTimer);
      if (!generateAbort.signal.aborted) generateAbort.abort();
    }
  }

  reportUnsupportedServiceTier(params, warnings);
  reportPromptTokens({ onPromptTokens: options?.onPromptTokens }, usage);
  const requiredProps = toolRequiredProps(params.tools);
  return {
    id: translatedMessageId(), type: 'message', role: 'assistant', model: modelId,
    content: [
      ...(text ? [{ type: 'text', text }] : []),
      ...toolCalls.map(tc => ({
        type: 'tool_use',
        id: encodeToolUseId(tc.toolCallId, grabRoundTripSignature(tc as FullStreamPart)),
        name: tc.toolName,
        input: representableToolInput(sanitizeToolInput(tc.input ?? {}, requiredProps.get(tc.toolName))),
      })),
    ],
    stop_reason: finishReason === 'tool-calls' ? 'tool_use' : 'end_turn',
    usage: toAnthropicUsage(usage),
  };
}

